import { collabStateManager } from '@databases/collab-state-manager';
import { associateMediaIdsWithCurrentCollection } from '@databases/media-client/media-client-core';
import { type Database, type MutableDatabase } from '@databases/model/database';
import { type MutableEntry } from '@databases/model/entry';
import { FieldType } from '@databases/model/field';
import { type MutableValue, type Value } from '@databases/model/value';
import { useEffect } from 'react';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { extractMediaIdFromUnassociatedPlaceholder } from '@canvas/media-id';
import { CanvasTransactionOrigin } from '@canvas/transaction';

const entries = (database: Database) => [
  database.defaultEntry,
  ...Object.values(database.entriesById)
];

function isAttachmentValue(
  value: MutableValue<FieldType> | undefined
): value is MutableValue<FieldType.Attachment>;
function isAttachmentValue(
  value: Value<FieldType> | undefined
): value is Value<FieldType.Attachment>;
function isAttachmentValue(
  value: Value<FieldType> | MutableValue<FieldType> | undefined
): boolean {
  return value?.type === FieldType.Attachment;
}

export function getUnassociatedMediaReplacements(
  database: Database
): ReadonlyMap<string, string> {
  const replacements = new Map<string, string>();

  for (const entry of entries(database)) {
    for (const fieldId of database.fields) {
      const value = entry.getValue(fieldId);
      if (!isAttachmentValue(value)) {
        continue;
      }

      for (const attachment of value.value) {
        const mediaId = extractMediaIdFromUnassociatedPlaceholder(attachment.fileId);
        if (mediaId) {
          replacements.set(attachment.fileId, mediaId);
        }
      }
    }
  }

  return replacements;
}

function replaceEntryMediaIds(
  entry: MutableEntry,
  fieldIds: readonly string[],
  replacements: ReadonlyMap<string, string>
) {
  for (const fieldId of fieldIds) {
    const value = entry.getValue(fieldId);
    if (!isAttachmentValue(value)) {
      continue;
    }

    let changed = false;
    const attachments = value.value.map((attachment) => {
      const mediaId = replacements.get(attachment.fileId);
      if (!mediaId) {
        return attachment;
      }

      changed = true;
      return { ...attachment, fileId: mediaId };
    });

    if (changed) {
      value.setTo(attachments);
    }
  }
}

export function persistAssociatedMediaIds(
  database: Database,
  replacements: ReadonlyMap<string, string>
) {
  database.mutate((mutableDatabase: MutableDatabase) => {
    replaceEntryMediaIds(
      mutableDatabase.defaultEntry,
      mutableDatabase.fields,
      replacements
    );
    for (const entry of Object.values(mutableDatabase.entriesById)) {
      replaceEntryMediaIds(entry, mutableDatabase.fields, replacements);
    }
  }, CanvasTransactionOrigin.TriggerObservers);
}

export function useUnassociatedMediaRepair({
  contentId,
  isLoaded
}: {
  contentId: string;
  isLoaded: boolean;
}) {
  useEffect(() => {
    if (!isFeatureFlagEnabled('uiBrieImprovements') || !isLoaded) {
      return;
    }

    const collabManager = collabStateManager.getCollabManager(contentId);
    if (!collabManager || collabManager.isReadOnly) {
      return;
    }

    const replacements = getUnassociatedMediaReplacements(collabManager.database);
    if (replacements.size === 0) {
      return;
    }

    let active = true;

    const repair = async () => {
      const associated = await associateMediaIdsWithCurrentCollection(contentId, [
        ...new Set(replacements.values())
      ]);
      if (active && associated) {
        persistAssociatedMediaIds(collabManager.database, replacements);
      }
    };

    void repair();

    return () => {
      active = false;
    };
  }, [contentId, isLoaded]);
}
